import React from 'react';

import type { ConsultationFieldDefinition } from '../types/Consultation';
import {
  uploadConsultationFieldAttachment,
  deleteConsultationFieldAttachment,
} from '../services/consultationService';
import {
  buildAttachmentsAcceptAttribute,
  formatAttachmentSize,
  isImageAttachment,
  parseAttachmentsValue,
  parseConsultationAttachmentsSchema,
  serializeAttachmentsValue,
  validateLocalAttachmentFile,
  type ConsultationAttachmentItem,
} from '../consultationAttachmentsSchema';
import styles from './ConsultationAttachmentsFieldInput.module.css';

interface ConsultationAttachmentsFieldInputProps {
  field: ConsultationFieldDefinition;
  consultationId?: string;
  value: string;
  onChange: (value: string) => void;
  onPendingAttachmentsChange?: (files: File[]) => void;
  disabled?: boolean;
}

export const ConsultationAttachmentsFieldInput: React.FC<ConsultationAttachmentsFieldInputProps> = ({
  field,
  consultationId,
  value,
  onChange,
  onPendingAttachmentsChange,
  disabled = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [error, setError] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);

  const schema = React.useMemo(
    () => parseConsultationAttachmentsSchema(field.schemaJson),
    [field.schemaJson],
  );

  const parsedValue = React.useMemo(() => parseAttachmentsValue(value), [value]);
  const accept = React.useMemo(
    () => buildAttachmentsAcceptAttribute(schema?.allowedTypes ?? []),
    [schema?.allowedTypes],
  );

  React.useEffect(() => {
    onPendingAttachmentsChange?.(pendingFiles);
  }, [onPendingAttachmentsChange, pendingFiles]);

  const pendingPreviewUrls = React.useMemo(() => {
    const urls = new Map<number, string>();
    pendingFiles.forEach((file, index) => {
      if (isImageAttachment(file.type || 'application/octet-stream', file.name)) {
        urls.set(index, URL.createObjectURL(file));
      }
    });
    return urls;
  }, [pendingFiles]);

  React.useEffect(() => () => {
    pendingPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [pendingPreviewUrls]);

  if (!schema) {
    return (
      <div className={styles.error}>
        Attachment settings are missing or invalid for this field.
      </div>
    );
  }

  const maxFiles = schema.maxFiles ?? 10;
  const totalCount = parsedValue.attachments.length + pendingFiles.length;
  const allowDragDrop = schema.allowDragDrop !== false;

  const setPending = (next: File[]) => {
    setPendingFiles(next);
    onPendingAttachmentsChange?.(next);
  };

  const applyUpdatedValue = (attachments: ConsultationAttachmentItem[]) => {
    onChange(serializeAttachmentsValue({ attachments }));
  };

  const queuePendingFile = (file: File) => {
    const validationError = validateLocalAttachmentFile(schema, file, totalCount);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setPending([...pendingFiles, file]);
  };

  const uploadFile = async (file: File) => {
    if (!consultationId) {
      queuePendingFile(file);
      return;
    }

    const validationError = validateLocalAttachmentFile(schema, file, parsedValue.attachments.length);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');
    try {
      const updated = await uploadConsultationFieldAttachment(consultationId, field.id, file);
      onChange(serializeAttachmentsValue(parseAttachmentsValue(updated.value)));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload attachment.');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (files: FileList | File[] | null) => {
    if (disabled || !files || files.length === 0) {
      return;
    }

    for (const file of Array.from(files)) {
      if (totalCount >= maxFiles) {
        setError(`No more than ${maxFiles} attachment(s) are allowed.`);
        break;
      }

      await uploadFile(file);
    }
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    if (disabled) {
      return;
    }

    if (!consultationId) {
      applyUpdatedValue(parsedValue.attachments.filter((item) => item.id !== attachmentId));
      return;
    }

    setUploading(true);
    setError('');
    try {
      const updated = await deleteConsultationFieldAttachment(consultationId, field.id, attachmentId);
      onChange(serializeAttachmentsValue(parseAttachmentsValue(updated.value)));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to remove attachment.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePendingFile = (index: number) => {
    setPending(pendingFiles.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div>
      <div
        className={[
          styles.dropZone,
          dragActive ? styles.dropZoneActive : '',
          disabled ? styles.dropZoneDisabled : '',
        ].filter(Boolean).join(' ')}
        onDragEnter={(event) => {
          if (!allowDragDrop || disabled) return;
          event.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(event) => {
          if (!allowDragDrop || disabled) return;
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          if (!allowDragDrop || disabled) return;
          event.preventDefault();
          setDragActive(false);
        }}
        onDrop={(event) => {
          if (!allowDragDrop || disabled) return;
          event.preventDefault();
          setDragActive(false);
          void handleFiles(event.dataTransfer.files);
        }}
      >
        <p className={styles.hint}>
          {allowDragDrop
            ? 'Drag and drop files here, or choose files to upload.'
            : 'Choose files to upload.'}
          {' '}
          Up to {maxFiles} files, max {Math.round((schema.maxFileSizeBytes ?? 0) / (1024 * 1024))} MB each.
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={disabled || uploading || totalCount >= maxFiles}
            onClick={() => inputRef.current?.click()}
          >
            Choose files
          </button>
          <input
            ref={inputRef}
            className={styles.fileInput}
            type="file"
            multiple
            accept={accept}
            disabled={disabled || uploading || totalCount >= maxFiles}
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
      </div>

      {!consultationId && pendingFiles.length > 0 ? (
        <p className={styles.hint}>Selected files will upload after the consultation is saved.</p>
      ) : null}

      {(parsedValue.attachments.length > 0 || pendingFiles.length > 0) ? (
        <div className={styles.list}>
          {parsedValue.attachments.map((attachment) => (
            <div key={attachment.id} className={styles.item}>
              {isImageAttachment(attachment.contentType, attachment.fileName) ? (
                <a
                  className={styles.thumbnailLink}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${attachment.fileName}`}
                >
                  <img
                    className={styles.thumbnail}
                    src={attachment.url}
                    alt={attachment.fileName}
                    loading="lazy"
                  />
                </a>
              ) : null}
              <div className={styles.itemMeta}>
                <a className={styles.itemName} href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.fileName}
                </a>
                <span className={styles.itemDetails}>{formatAttachmentSize(attachment.sizeBytes)}</span>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={disabled || uploading}
                onClick={() => void handleRemoveAttachment(attachment.id)}
              >
                Remove
              </button>
            </div>
          ))}

          {pendingFiles.map((file, index) => (
            <div key={`${file.name}-${file.size}-${index}`} className={styles.item}>
              {pendingPreviewUrls.has(index) ? (
                <img
                  className={styles.thumbnail}
                  src={pendingPreviewUrls.get(index)}
                  alt={file.name}
                />
              ) : null}
              <div className={styles.itemMeta}>
                <span className={styles.itemName}>{file.name}</span>
                <span className={styles.itemDetails}>{formatAttachmentSize(file.size)} • pending upload</span>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={disabled || uploading}
                onClick={() => handleRemovePendingFile(index)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {uploading ? <p className={styles.uploading}>Uploading…</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
};

export default ConsultationAttachmentsFieldInput;
